import { useState } from 'nuxt/app';

type ToastColor = 'info' | 'error' | 'success' | 'warning';

interface ToastItem {
    id: string;
    message: string;
    color: ToastColor;
    timeout: number;
    isAuthError: boolean;
    visible: boolean;
}

export function useToast() {
    // ✅ 다중 토스트 지원을 위한 배열 상태
    const toasts = useState<ToastItem[]>('toasts', () => []);

    // 기존 단일 토스트 상태 (하위 호환성을 위해 유지)
    const visible = useState<boolean>('toastVisible', () => false);
    const message = useState<string>('toastMessage', () => '');
    const color = useState<ToastColor>('toastColor', () => 'success');
    const timeout = useState<number>('toastTimeout', () => 3000);
    const isAuthError = useState<boolean>('toastAuthError', () => false);

    /**
     * ✅ 토스트 메시지 표시 (기존 방식 - 하나만 표시)
     */
    const showToast = (msg: string, col: ToastColor = 'success', to: number = 3000, authError: boolean = false) => {
        message.value = msg;
        color.value = col;
        timeout.value = to;
        isAuthError.value = authError;
        visible.value = true;
    };

    /**
     * ✅ 다중 토스트 메시지 추가
     */
    const addToast = (msg: string, col: ToastColor = 'success', to: number = 3000, authError: boolean = false) => {
        const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newToast: ToastItem = {
            id,
            message: msg,
            color: col,
            timeout: to,
            isAuthError: authError,
            visible: true
        };

        // 배열에 추가
        toasts.value = [...toasts.value, newToast];

        // 타임아웃 후 visible을 false로 변경
        setTimeout(() => {
            const index = toasts.value.findIndex(t => t.id === id);
            if (index !== -1) {
                // Vue reactivity를 위해 배열 전체를 새로 할당
                toasts.value = toasts.value.map((t, i) =>
                    i === index ? { ...t, visible: false } : t
                );

                // 애니메이션 완료 후 제거
                setTimeout(() => {
                    removeToast(id);
                }, 300);
            }
        }, to);
    };

    /**
     * ✅ 특정 토스트 제거
     */
    const removeToast = (id: string) => {
        // Vue reactivity를 위해 filter로 새 배열 생성
        toasts.value = toasts.value.filter(toast => toast.id !== id);
    };

    /**
     * ✅ 토스트 메시지 숨김 (기존 방식)
     */
    const hideToast = () => {
        visible.value = false;
    };

    /**
     * ✅ 모든 토스트 제거
     */
    const clearAllToasts = () => {
        toasts.value = [];
    };

    return {
        // 기존 단일 토스트 (하위 호환성)
        visible,
        message,
        color,
        timeout,
        isAuthError,
        showToast,
        hideToast,
        
        // 다중 토스트
        toasts,
        addToast,
        removeToast,
        clearAllToasts,
    };
}
